import type { ProjectStatus } from "@shared/types";

/**
 * Canonical display labels for the three project statuses. Use this
 * everywhere a project status is rendered to keep casing/spacing consistent.
 */
export const PROJECT_STATUS_LABEL: Record<ProjectStatus, string> = {
  active: "Active",
  on_hold: "On hold",
  archived: "Archived",
};

export type ProjectPillVariant = "backlog" | "todo" | "doing" | "done";

/**
 * Maps a project status to a Pill variant the design system understands.
 * Accepts any string so callers receiving loosely-typed server data still
 * get a safe fallback (`backlog`) for unknown values.
 *
 * - `active` → `doing` (green)
 * - `on_hold` → `backlog` (grey) — NOT `done`, which previously caused
 *   on-hold projects to render with the green "done" pill.
 * - `archived` → `done`
 */
export function projectPillVariant(
  status: ProjectStatus | string,
): ProjectPillVariant {
  switch (status) {
    case "active":
      return "doing";
    case "on_hold":
      return "backlog";
    case "archived":
      return "done";
    default:
      return "backlog";
  }
}

/**
 * Safe title-case label for any status string. Returns "" for empty/null.
 * Used by routes that may receive looser server data than the strict enum.
 *
 * Replaces underscores with spaces so `on_hold` renders as `On hold`
 * rather than the raw enum value.
 */
export function statusLabel(status: string | null | undefined): string {
  if (!status) return "";
  return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ");
}
